import { createSlice, Slice } from '@reduxjs/toolkit';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import {
	formateApiDateValue,
	formateNullableEnumValue,
} from 'modules/utilities/Helpers';
import { RootStateOrAny } from 'react-redux';
import { NotificationsRedirectToScreen } from '../constants/enums';
import {
	INotificationDetails,
	INotificationListItem,
} from '../interfaces/notification';
import { IOnboardingQuestionAnswerFilter } from '../../shared/interfaces/onboardingQuestionAnswerFilter';

export const NOTIFICATION_KEY_IN_STORE = 'notifications';

export interface INotificationsState {
	isLoading: boolean;
	notifications: IPaginatedData<INotificationListItem>;
	deleteNotification: IReqStatus;
	addNotification: IReqStatus;
	updateNotification: {
		itemToUpdate: {
			isLoading: boolean;
			data?: INotificationDetails;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
	searchUsersQuestionsAnswers: {
		isLoading: boolean;
		result: IOnboardingQuestionAnswerFilter[];
	};
}

const initialState: INotificationsState = {
	notifications: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	isLoading: false,
	addNotification: {
		success: false,
		error: false,
	},
	deleteNotification: {
		success: false,
		error: false,
	},
	updateNotification: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
	searchUsersQuestionsAnswers: {
		isLoading: false,
		result: [],
	},
};

const notificationSlice: Slice = createSlice({
	name: NOTIFICATION_KEY_IN_STORE,
	initialState,
	reducers: {
		getAllNotifications: state => {
			state.isLoading = true;
		},
		getAllNotificationsSuccess: (state, action) => {
			const { items, meta } = action.payload;
			state.notifications = {
				items: items.map((item: INotificationListItem) => ({
					...item,
					scheduledAt: formateApiDateValue(item.scheduledAt),
				})),
				meta,
			};
			state.isLoading = false;
		},
		getAllNotificationsFailure: state => {
			state.isLoading = false;
		},
		deleteNotification: state => {
			state.deleteNotification = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteNotificationSuccess: (state: RootStateOrAny) => {
			state.deleteNotification = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteNotificationFailure: (state: RootStateOrAny) => {
			state.deleteNotification = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteNotificationStatus: state => {
			state.deleteNotification = {
				success: false,
				error: false,
			};
		},
		addNotification: state => {
			state.isLoading = true;
			state.addNotification = {
				success: false,
				error: false,
			};
		},
		addNotificationSuccess: (state: RootStateOrAny) => {
			state.addNotification = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addNotificationFailure: state => {
			state.addNotification = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getNotificationToUpdate: state => {
			state.updateNotification.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getNotificationToUpdateSuccess: (state, action) => {
			state.updateNotification.itemToUpdate = {
				isLoading: false,
				data: {
					...action.payload,
					scheduledAt: formateApiDateValue(action.payload.scheduledAt),
					endUsersFilter: {
						...action.payload.endUsersFilter,
						currentRegion: formateNullableEnumValue(
							action.payload.endUsersFilter.currentRegion,
						),
						gender: formateNullableEnumValue(
							action.payload.endUsersFilter.gender,
						),
					},

					redirectToScreen:
						action.payload.redirectToScreen ||
						NotificationsRedirectToScreen.MAP,
				},
				status: {
					success: true,
					error: false,
				},
			};
		},
		getNotificationToUpdateFailure: state => {
			state.updateNotification.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateNotification: state => {
			state.isLoading = true;
			state.updateNotification.status = {
				success: false,
				error: false,
			};
		},
		updateNotificationSuccess: state => {
			state.isLoading = false;
			state.updateNotification.status = {
				success: true,
				error: false,
			};
		},
		updateNotificationFailure: state => {
			state.isLoading = false;
			state.updateNotification.status = {
				success: false,
				error: true,
			};
		},

		searchUsersQuestionsAnswers: state => {
			state.searchUsersQuestionsAnswers = {
				isLoading: true,
				result: [],
			};
		},
		setSearchUsersQuestionsAnswersResult: (state, action) => {
			state.searchUsersQuestionsAnswers = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSearchUsersQuestionsAnswersResult: state => {
			state.searchUsersQuestionsAnswers = {
				isLoading: false,
				result: [],
			};
		},

		resetNotificationData: () => {
			return initialState;
		},
	},
});

export const {
	getAllNotifications,
	getAllNotificationsSuccess,
	getAllNotificationsFailure,

	deleteNotification,
	deleteNotificationSuccess,
	deleteNotificationFailure,

	resetDeleteNotificationStatus,
	addNotification,
	addNotificationSuccess,
	addNotificationFailure,
	resetNotificationData,

	getNotificationToUpdate,
	getNotificationToUpdateSuccess,
	getNotificationToUpdateFailure,

	updateNotification,
	updateNotificationSuccess,
	updateNotificationFailure,

	searchUsersQuestionsAnswers,
	setSearchUsersQuestionsAnswersResult,
	resetSearchUsersQuestionsAnswersResult,
} = notificationSlice.actions;

export const notificationStateSelector = (
	state: RootStateOrAny,
): INotificationsState => state[NOTIFICATION_KEY_IN_STORE];

export default notificationSlice;
